<script lang="ts">
	import {
		Alert,
		ButtonV2,
		Container,
		Loader,
		Modal,
		Stack,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import { getSubUnits, removeSubUnit, renewSubUnit } from 'src/services/iotu/house-management';
	import { currentUnitId, refreshHomes, sub_units } from 'src/stores/account/home';
	import SubUnitItem from './SubUnitItem.svelte';
	import InviteSubUnitForm from './InviteSubUnitForm.svelte';
	import { format } from 'svelte-i18n';
	import { get } from 'svelte/store';
	import type { SubUnitData, SubUnitMeta } from './interfaces/SubUnit';
	import { replace } from 'svelte-spa-router';
	import { isSubUnit } from 'src/helpers/user_helpers';
	import UnitSelectionModal from 'src/components/UnitSelectionModal.svelte';
	import { clearAllData } from 'src/helpers/account_helpers';
	import { user_data } from 'src/stores/account/user';
	import type { AvailableSubUnits, AvailableUnits } from 'src/types/responses/usersAuthResponse';
	import { switchUnit } from 'src/services/auth_v2';
	import { invalidateAccountSessions } from 'src/services/unit_token_cache';

	let isLoading = false;

	let showUnitSelectionModal = false;
	let showLeaveModal = false;
	let isLeaving = false;

	let showInviteModal = false;
	let subUnitToEdit: Record<string, SubUnitData> | null = null;
	let inviteModalTitle = '';

	let showRemoveModal = false;
	let subUnitToRemove: string | null = null;

	let showRenewModal = false;
	let subUnitToRenew: string | null = null;

	// Tracks the unit the current `sub_units` snapshot belongs to. Required so we
	// re-fetch when the active home is switched while the page stays mounted
	// (FE-1595): otherwise stale entries from the previous house keep rendering.
	let loadedUnitId: string | null = null;

	$: subUnitEntriesArray = Object.entries($sub_units) as [string, SubUnitData][];
	$: ownedUnits = $user_data.available_units ?? {};
	$: subUnitEntries = $user_data.available_sub_units ?? {};
	$: otherOwnedUnits = Object.fromEntries(
		Object.entries(ownedUnits).filter(([id]) => id !== $currentUnitId),
	) as AvailableUnits;

	$: otherSharedUnits = Object.fromEntries(
		Object.entries(subUnitEntries).filter(([id]) => id !== $currentUnitId),
	) as AvailableSubUnits;

	$: hasOtherHomes =
		Object.keys(otherOwnedUnits).length > 0 || Object.keys(otherSharedUnits).length > 0;
	$: hasOwnedHomes = Object.keys(ownedUnits).length > 0;

	$: void loadSubUnitsForActiveHome($currentUnitId);

	function extractSubId(key: string): string {
		return key.replace(/^sub_/, '');
	}

	async function loadSubUnitsForActiveHome(unitId: string): Promise<void> {
		if (!unitId || unitId === loadedUnitId) return;
		loadedUnitId = unitId;
		await loadSubUnits();
	}

	async function loadSubUnits(): Promise<void> {
		isLoading = true;
		// Drop the previous home's entries immediately so the list cannot flash
		// stale rows while the new request is in flight.
		sub_units.reset();
		try {
			const [subUnitsResponse] = await Promise.all([getSubUnits(), refreshHomes()]);

			if (!subUnitsResponse.isok) {
				throw new Error($format('error_global', { default: 'Something went wrong.' }));
			}
			sub_units.set(subUnitsResponse.data ?? {});
		} catch (err: any) {
			// fail silently
			// Refactor this to use a more robust error handling strategy
			console.log('[SubUnitManagement] Error loading sub units.', err);
		} finally {
			isLoading = false;
		}
	}

	function onInviteClick(): void {
		subUnitToEdit = null;
		inviteModalTitle = $format('shared_invite_sub_unit', { default: 'Invite to Home' });
		showInviteModal = true;
	}

	function onEditSubUnit(id: string, data: SubUnitData): void {
		subUnitToEdit = { [id]: data };
		inviteModalTitle = $format('shared_edit_sub_unit_access', { default: 'Edit Home Access' });
		showInviteModal = true;
	}

	function onRemoveSubUnit(id: string): void {
		subUnitToRemove = id;
		showRemoveModal = true;
	}

	function onRenewSubUnit(id: string): void {
		subUnitToRenew = id;
		showRenewModal = true;
	}

	async function confirmRemove(): Promise<void> {
		if (!subUnitToRemove) return;

		const sub = extractSubId(subUnitToRemove);
		const id = subUnitToRemove;
		isLoading = true;
		showRemoveModal = false;

		try {
			const response = await removeSubUnit(sub);
			if (!response.isok) {
				throw new Error($format('error_global', { default: 'Something went wrong.' }));
			}
			sub_units.remove(id);
			toast.open({
				type: 'success',
				title: $format('shared_remove_sub_unit_toaster', {
					default: 'User removed from home.',
				}),
				duration: 2000,
			});
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title: err?.message ?? $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLoading = false;
			subUnitToRemove = null;
		}
	}

	async function confirmRenew(): Promise<void> {
		if (!subUnitToRenew) return;

		const sub = extractSubId(subUnitToRenew);
		const id = subUnitToRenew;
		isLoading = true;
		showRenewModal = false;

		try {
			const response = await renewSubUnit(sub);
			if (!response.isok) {
				throw new Error($format('error_global', { default: 'Something went wrong.' }));
			}

			const current = get(sub_units);
			if (current[id]) {
				const updated: SubUnitData = [
					{ ...current[id][0] } as SubUnitMeta,
					current[id][1],
					current[id][2],
					current[id][3],
					current[id][4],
					current[id][5],
				];
				sub_units.update({ [id]: updated });
			}

			toast.open({
				type: 'success',
				title: $format('shared_reinvite_sub_unit_toaster', {
					default: 'Invitation resent.',
				}),
				duration: 2000,
			});
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title: err?.message ?? $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLoading = false;
			subUnitToRenew = null;
		}
	}

	function onFormModalClose(
		event: CustomEvent<{ sub: string; meta: SubUnitMeta; rights: string } | null>,
	): void {
		const detail = event.detail;
		showInviteModal = false;
		subUnitToEdit = null;

		if (!detail) return;

		const parsedRights = JSON.parse(detail.rights);
		sub_units.update({
			[`sub_${detail.sub}`]: [
				detail.meta,
				parsedRights[0],
				parsedRights[1],
				parsedRights[2],
				parsedRights[3],
				parsedRights[4],
			],
		});
	}

	function onLeaveHomeClick(): void {
		showLeaveModal = true;
	}

	async function confirmLeaveHome(): Promise<void> {
		if (hasOtherHomes) {
			showLeaveModal = false;
			showUnitSelectionModal = true;
		} else {
			await leaveAndLogout();
		}
	}

	async function leaveAndLogout(): Promise<void> {
		isLeaving = true;
		try {
			const response = await removeSubUnit();
			if (!response.isok) {
				throw new Error($format('error_global', { default: 'Something went wrong.' }));
			}

			toast.open({
				type: 'success',
				title: $format('shared_leave_home_success', {
					default: 'You have left the shared home.',
				}),
				duration: 2000,
			});

			clearAllData();
			replace('/login');
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title: err?.message ?? $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLeaving = false;
			showLeaveModal = false;
		}
	}

	async function onSelectUnit(event: CustomEvent<{ unitId: string; subId?: number }>): Promise<void> {
		const { unitId, subId } = event.detail;
		isLoading = true;

		try {
			const response = await removeSubUnit();
			if (!response.isok) {
				throw new Error($format('error_global', { default: 'Something went wrong.' }));
			}

			// Left this shared home: drop the account's cached sessions so the
			// just-left home is not re-served from cache on a later switch.
			invalidateAccountSessions(get(user_data).email);

			await switchUnit(unitId, subId?.toString());

			toast.open({
				type: 'success',
				title: $format('shared_leave_home_switched', {
					default: 'You have left the shared home and switched to another home.',
				}),
				duration: 2000,
			});

			replace('/');
			showUnitSelectionModal = false;
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title: err?.message ?? $format('error_global', { default: 'Something went wrong.' }),
				duration: 2000,
			});
		} finally {
			isLoading = false;
		}
	}
</script>

{#if isSubUnit()}
	<!-- Leave Shared Home section -->
	<div class="d-flex flex-column gap-s mt-m justify-content-between align-items-center">
		<Text styleType="body" class="mb-xs">
			{$format('shared_leave_home_section_title', { default: 'Leave Shared Home' })}
		</Text>
		<ButtonV2
			variant="danger"
			size="m"
			loading={isLeaving}
			on:click={onLeaveHomeClick}
			aria-label={$format('shared_leave_home_button', { default: 'Leave this home' })}
		>
			{$format('shared_leave_home_button', { default: 'Leave this home' })}
		</ButtonV2>
	</div>
	<Alert
		type="info"
		class="mt-s"
		heading={$format('shared_leave_home_section_info', {
			default:
				'You can leave this shared home at any time. You will lose access to all devices and scenes.',
		})}
	/>
	<!-- Leave Home Confirmation Modal -->
	<Modal
		showModal={showLeaveModal}
		on:close={() => (showLeaveModal = false)}
		title={$format('shared_leave_home_alert_header', { default: 'Leave Shared Home' })}
	>
		<svelte:fragment slot="content">
			<Text>
				{#if hasOtherHomes}
					{$format('shared_leave_home_alert_info_with_switch', {
						default:
							'Are you sure you want to leave this shared home? You will lose access to all devices and scenes. You can switch to another home after leaving.',
					})}
				{:else}
					{$format('shared_leave_home_alert_info_logout', {
						default:
							'Are you sure you want to leave this shared home? You will lose access to all devices and scenes and will be logged out.',
					})}
				{/if}
			</Text>
		</svelte:fragment>

		<div slot="footer" class="d-flex justify-content-end">
			<ButtonV2
				width="m"
				variant="secondary"
				class="mr-s"
				on:click={() => (showLeaveModal = false)}
				aria-label={$format('button_cancel', { default: 'Cancel' })}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</ButtonV2>
			<ButtonV2
				width="m"
				variant="danger"
				loading={isLeaving}
				on:click={confirmLeaveHome}
				aria-label={hasOtherHomes
					? $format('shared_leave_home_continue_button', { default: 'Continue' })
					: $format('shared_leave_home_confirm_button', { default: 'Leave' })}
			>
				{#if hasOtherHomes}
					{$format('shared_leave_home_continue_button', { default: 'Continue' })}
				{:else}
					{$format('shared_leave_home_confirm_button', { default: 'Leave' })}
				{/if}
			</ButtonV2>
		</div>
	</Modal>

	<!-- Unit Selection Modal (shown after confirming leave when user has other homes) -->
	<UnitSelectionModal
		showModal={showUnitSelectionModal}
		units={otherOwnedUnits}
		subUnits={otherSharedUnits}
		{isLoading}
		on:select={onSelectUnit}
	/>
{:else}
	<Stack
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
		gap="m"
		class="mt-m mb-s align-items-center"
	>
		<div>
			<Text styleType="body" class="mb-s">
				{$format('settings_sub_unit_management_title', { default: 'Shared Home Access' })}
			</Text>
			{#if !isLoading && subUnitEntriesArray.length === 0}
				<Text styleType="body-small" class="text-color-muted">
					{$format('shared_sub_units_empty_message', {
						default: 'No one has access to your home yet.',
					})}
				</Text>
			{/if}
		</div>
		<Stack direction="column" directionMobile="row" gap="xs">
			<ButtonV2
				icon="fas fa-plus"
				variant="secondary"
				size="m"
				loading={isLoading}
				on:click={onInviteClick}
			>
				{$format('shared_invite_sub_unit', { default: 'Invite to Home' })}
			</ButtonV2>
		</Stack>
	</Stack>

	{#if isLoading}
		<div class="d-flex justify-content-center">
			<Loader size="m" />
		</div>
	{/if}

	{#each subUnitEntriesArray as [id, data] (id)}
		<div class="mb-s">
			<SubUnitItem
				subUnit={data}
				on:edit={() => onEditSubUnit(id, data)}
				on:remove={() => onRemoveSubUnit(id)}
				on:renew={() => onRenewSubUnit(id)}
			/>
		</div>
	{/each}
{/if}

<!-- Invite / Edit Modal -->
<Modal
	showModal={showInviteModal}
	on:close={() => onFormModalClose(new CustomEvent('close', { detail: null }))}
	title={inviteModalTitle}
	removeScrollPadding
>
	<Container slot="content" padding="0">
		<InviteSubUnitForm {subUnitToEdit} on:closeModal={onFormModalClose} />
	</Container>
</Modal>

<!-- Remove Confirmation Modal -->
<Modal
	showModal={showRemoveModal}
	on:close={() => (showRemoveModal = false)}
	title={$format('shared_remove_sub_unit_alert_header', { default: 'Remove from Home' })}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('shared_remove_sub_unit_alert_info', {
				default:
					'Are you sure you want to remove this person from your home? This action cannot be undone.',
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 width="m" variant="secondary" class="mr-s" on:click={() => (showRemoveModal = false)}>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2 width="m" variant="danger" loading={isLoading} on:click={confirmRemove}>
			{$format('button_remove', { default: 'Remove' })}
		</ButtonV2>
	</div>
</Modal>

<!-- Renew Confirmation Modal -->
<Modal
	showModal={showRenewModal}
	on:close={() => (showRenewModal = false)}
	title={$format('shared_reinvite_sub_unit_alert_header', { default: 'Resend Invitation' })}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('shared_reinvite_sub_unit_alert_info', {
				default:
					'Are you sure you want to resend the invitation? A new invitation email will be sent to the user.',
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 width="m" variant="secondary" class="mr-s" on:click={() => (showRenewModal = false)}>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2 width="m" loading={isLoading} on:click={confirmRenew}>
			{$format('button_confirm', { default: 'Confirm' })}
		</ButtonV2>
	</div>
</Modal>
